import { MAX_TOPICS_PER_POST } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/constants";
import { useTopicsMultiSelect } from "@circle-react/components/QuickPostV3/hooks/useTopicsMultiSelect";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { MultiSelect } from "@circleco/compass/components/MultiSelect";
import { type KeyboardEvent, useCallback, useEffect, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import { normalizeTopicIds } from "../helpers";
import type { ImagePostFormValues } from "../types";

interface TopicsFieldProps {
  creatable?: boolean;
  inline?: boolean;
  spaceId: string;
}

export const TopicsField = ({
  spaceId,
  creatable = false,
  inline = false,
}: TopicsFieldProps) => {
  const { control } = useFormContext<ImagePostFormValues>();
  const { field } = useController<ImagePostFormValues, "topics">({
    control,
    name: "topics",
  });
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const selectedIds = normalizeTopicIds(field.value);

  const {
    createTopic,
    isCreatable: canCreate,
    dropdownOptions,
    handleValueChange,
    isLoading,
    onInputValueChange,
    selectedOptions,
    shouldShow,
  } = useTopicsMultiSelect({
    spaceId,
    value: selectedIds,
    onChange: field.onChange,
    isCreatable: creatable,
  });

  const hasSearchQuery = inputValue.trim().length > 0;
  const canOpenDropdown =
    isLoading || dropdownOptions.length > 0 || hasSearchQuery;

  useEffect(() => {
    if (isOpen && !canOpenDropdown) {
      setIsOpen(false);
    }
  }, [canOpenDropdown, isOpen]);

  const handleInputValueChange = useCallback(
    (value: string) => {
      setInputValue(value);
      onInputValueChange(value);
      if (value.trim().length > 0) {
        setIsOpen(true);
      }
    },
    [onInputValueChange],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open && canOpenDropdown);
    },
    [canOpenDropdown],
  );

  if (!shouldShow) return null;

  const handleKeyDownCapture = (event: KeyboardEvent) => {
    if (event.key !== "Enter" || isOpen) return;
    event.preventDefault();
  };

  return (
    <div
      className={classNames(
        "flex w-full min-w-0",
        !inline && "py-2",
        "[&_[data-closed]]:hidden",
      )}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <MultiSelect<number>
        data-testid="topics-list"
        className="flex-1"
        naked
        icon="tag"
        open={isOpen}
        onOpenChange={handleOpenChange}
        side="top"
        align="start"
        options={dropdownOptions}
        value={selectedOptions}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={handleInputValueChange}
        isLoading={isLoading}
        maxSelections={MAX_TOPICS_PER_POST}
        placeholder={t("quick_post_v2.topics.search", {
          count: MAX_TOPICS_PER_POST,
        })}
        searchPlaceholder={t("search_v2.filters.search_topics")}
        emptyMessage={t("quick_post_v2.topics.no_results")}
        creatable={canCreate}
        onCreateOption={name =>
          createTopic(name, {
            onSuccess: () => setInputValue(""),
          })
        }
        loadingMessage={t("quick_post_v2.topics.loading")}
      />
    </div>
  );
};
